import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  QueuedPaymentCallback, 
  SyncResult, 
  OFFLINE_QUEUE_KEY, 
  MAX_RETRY_ATTEMPTS,
  RETRY_DELAY_MS 
} from '@/types/offlineSync';
import { supabase } from '@/integrations/supabase/client';
import { autoReconcilePayment } from '@/lib/cardReconciliationService';
import { toast } from '@/hooks/use-toast';

interface UseOfflineSyncQueueReturn {
  queuedItems: QueuedPaymentCallback[];
  pendingCount: number;
  isSyncing: boolean;
  queueItem: (item: Omit<QueuedPaymentCallback, 'id' | 'attempts' | 'lastAttempt' | 'createdAt'>) => void;
  processQueue: () => Promise<SyncResult[]>;
  removeItem: (id: string) => void;
  clearQueue: () => void;
  retryItem: (id: string) => Promise<SyncResult>;
}

/**
 * Hook for managing offline payment callback synchronization queue
 * Persists to localStorage and auto-syncs when online
 */
export function useOfflineSyncQueue(): UseOfflineSyncQueueReturn {
  const [queuedItems, setQueuedItems] = useState<QueuedPaymentCallback[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load queue from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(OFFLINE_QUEUE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as QueuedPaymentCallback[];
        setQueuedItems(parsed);
      } catch (error) {
        console.error('[OfflineSync] Failed to parse stored queue:', error);
        localStorage.removeItem(OFFLINE_QUEUE_KEY);
      }
    }
  }, []);

  // Persist queue to localStorage whenever it changes
  useEffect(() => {
    if (queuedItems.length > 0) {
      localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queuedItems));
    } else {
      localStorage.removeItem(OFFLINE_QUEUE_KEY);
    }
  }, [queuedItems]);

  // Auto-sync when coming back online
  useEffect(() => {
    const handleOnline = () => {
      console.log('[OfflineSync] Network online, processing queue...');
      if (queuedItems.length > 0) {
        toast({
          title: 'Conexão restaurada',
          description: `Sincronizando ${queuedItems.length} pagamento(s) pendente(s)...`,
        });
        processQueue();
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [queuedItems.length]);

  // Schedule retry for failed items
  useEffect(() => {
    const itemsNeedingRetry = queuedItems.filter(
      item => item.attempts > 0 && item.attempts < MAX_RETRY_ATTEMPTS
    );

    if (itemsNeedingRetry.length > 0 && navigator.onLine) {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      
      syncTimeoutRef.current = setTimeout(() => {
        console.log('[OfflineSync] Scheduled retry triggered');
        processQueue();
      }, RETRY_DELAY_MS);
    }

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [queuedItems]);

  /**
   * Add a new item to the sync queue
   */
  const queueItem = useCallback((
    item: Omit<QueuedPaymentCallback, 'id' | 'attempts' | 'lastAttempt' | 'createdAt'>
  ) => {
    const newItem: QueuedPaymentCallback = {
      ...item,
      id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      attempts: 0,
      lastAttempt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    };

    setQueuedItems(prev => [...prev, newItem]);
    
    console.log('[OfflineSync] Item queued:', newItem.transactionId);
  }, []);

  /**
   * Process a single queued item
   */
  const processItem = async (item: QueuedPaymentCallback): Promise<SyncResult> => {
    try {
      // Find pending payment record
      const { data: pendingPayment, error: findError } = await supabase
        .from('pending_external_payments')
        .select('*, sales(company_id, total_value)')
        .eq('transaction_id', item.transactionId)
        .single();

      if (findError || !pendingPayment) {
        throw new Error('Transação não encontrada');
      }

      if (item.status === 'success') {
        // Update pending payment to success
        const { error: updateError } = await supabase
          .from('pending_external_payments')
          .update({
            status: 'success',
            return_data: {
              nsu: item.nsu,
              authorizationCode: item.authorizationCode,
              cardBrand: item.cardBrand,
              cardLastDigits: item.cardLastDigits,
            },
            processed_at: new Date().toISOString(),
          })
          .eq('transaction_id', item.transactionId);

        if (updateError) throw updateError;

        // If linked to a sale, add the payment record
        if (pendingPayment.sale_id && pendingPayment.sales) {
          const { data: { user } } = await supabase.auth.getUser();
          const paymentMethod = item.paymentMethod === 'credit' ? 'credit_card' : 'debit_card';
          const paymentDate = new Date().toISOString();

          const { data: paymentResult } = await supabase
            .from('sale_payments')
            .insert({
              sale_id: pendingPayment.sale_id,
              company_id: pendingPayment.sales.company_id,
              payment_date: paymentDate,
              payment_method: paymentMethod,
              payment_value: item.amount,
              created_by: user?.id,
              notes: `${item.provider.toUpperCase()} - NSU: ${item.nsu || 'N/A'} (sync offline)`,
              external_payment_id: item.authorizationCode,
              authorization_code: item.authorizationCode,
              nsu: item.nsu,
              card_brand: item.cardBrand,
              card_last_digits: item.cardLastDigits,
              terminal_provider: item.provider,
              external_payment_status: 'approved',
            })
            .select('id')
            .single();

          // Auto-reconcile
          if (paymentResult?.id && (item.nsu || item.authorizationCode)) {
            await autoReconcilePayment(
              paymentResult.id,
              pendingPayment.sales.company_id,
              {
                nsu: item.nsu,
                authorizationCode: item.authorizationCode,
                amount: item.amount,
                paymentDate,
              },
              user?.id
            );
          }

          // Update sale payment status
          const { data: payments } = await supabase
            .from('sale_payments')
            .select('payment_value')
            .eq('sale_id', pendingPayment.sale_id);

          const totalPaid = payments?.reduce((sum, p) => sum + Number(p.payment_value), 0) || 0;

          let paymentStatus: 'pending' | 'partial' | 'paid' = 'pending';
          if (totalPaid >= Number(pendingPayment.sales.total_value)) {
            paymentStatus = 'paid';
          } else if (totalPaid > 0) {
            paymentStatus = 'partial';
          }

          await supabase
            .from('sales')
            .update({
              paid_value: totalPaid,
              payment_status: paymentStatus,
            })
            .eq('id', pendingPayment.sale_id);
        }
      } else {
        // Update as failed/cancelled
        await supabase
          .from('pending_external_payments')
          .update({
            status: item.status,
            error_message: item.errorMessage,
            processed_at: new Date().toISOString(),
          })
          .eq('transaction_id', item.transactionId);
      }

      return {
        id: item.id,
        success: true,
        syncedAt: new Date().toISOString(),
      };
    } catch (error) {
      console.error('[OfflineSync] Failed to sync item:', item.id, error);
      return {
        id: item.id,
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  };

  /**
   * Process all items in the queue
   */
  const processQueue = useCallback(async (): Promise<SyncResult[]> => {
    if (isSyncing || queuedItems.length === 0 || !navigator.onLine) {
      return [];
    }

    setIsSyncing(true);
    const results: SyncResult[] = [];
    const updatedItems: QueuedPaymentCallback[] = [];

    try {
      for (const item of queuedItems) {
        const result = await processItem(item);
        results.push(result);

        if (result.success) {
          // Item synced successfully, don't add to updated list
          console.log('[OfflineSync] Item synced:', item.transactionId);
        } else {
          // Increment attempts
          const updatedItem: QueuedPaymentCallback = {
            ...item,
            attempts: item.attempts + 1,
            lastAttempt: new Date().toISOString(),
          };

          if (updatedItem.attempts >= MAX_RETRY_ATTEMPTS) {
            console.error('[OfflineSync] Max retries reached:', item.transactionId);
            toast({
              title: 'Falha na sincronização',
              description: `Não foi possível sincronizar o pagamento ${item.transactionId}. Tentativas esgotadas.`,
              variant: 'destructive',
            });
          } else {
            updatedItems.push(updatedItem);
          }
        }
      }

      setQueuedItems(updatedItems);

      const successCount = results.filter(r => r.success).length;
      if (successCount > 0) {
        toast({
          title: 'Sincronização concluída',
          description: `${successCount} pagamento(s) sincronizado(s) com sucesso.`,
        });
      }
    } finally {
      setIsSyncing(false);
    }

    return results;
  }, [queuedItems, isSyncing]);

  /**
   * Retry a specific item
   */
  const retryItem = useCallback(async (id: string): Promise<SyncResult> => {
    const item = queuedItems.find(i => i.id === id);
    if (!item) {
      return { id, success: false, error: 'Item não encontrado' };
    }

    setIsSyncing(true);
    try {
      const result = await processItem(item);
      
      if (result.success) {
        setQueuedItems(prev => prev.filter(i => i.id !== id));
        toast({
          title: 'Pagamento sincronizado',
          description: 'O pagamento foi sincronizado com sucesso.',
        });
      } else {
        setQueuedItems(prev => prev.map(i => 
          i.id === id 
            ? { ...i, attempts: i.attempts + 1, lastAttempt: new Date().toISOString() }
            : i
        ));
      }
      
      return result;
    } finally {
      setIsSyncing(false);
    }
  }, [queuedItems]);

  /**
   * Remove an item from the queue
   */
  const removeItem = useCallback((id: string) => {
    setQueuedItems(prev => prev.filter(item => item.id !== id));
  }, []);

  /**
   * Clear the entire queue
   */
  const clearQueue = useCallback(() => {
    setQueuedItems([]);
    localStorage.removeItem(OFFLINE_QUEUE_KEY);
  }, []);

  return {
    queuedItems,
    pendingCount: queuedItems.length,
    isSyncing,
    queueItem,
    processQueue,
    removeItem,
    clearQueue,
    retryItem,
  };
}

/**
 * Check if an error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('failed to fetch') ||
      message.includes('networkerror') ||
      message.includes('connection') ||
      message.includes('offline') ||
      message.includes('timeout') ||
      message.includes('econnrefused') ||
      message.includes('enotfound')
    );
  }
  return !navigator.onLine;
}
